import { useCallback, useEffect, useRef, useState } from "react";
import { useCommunityInboxStore } from "@circle-react/hooks/chatsV2/store";
import type { Message } from "@circle-react/types/CommunityInbox/Message";

export const ACTIONS = {
  FIRST_LOAD: "FIRST_LOAD",
  PAGINATION: "PAGINATION",
  NEW_MESSAGE: "NEW_MESSAGE",
};

export const MESSAGE_CONTAINER_ID = "scrollableMessagesDiv";
const FIRST_LOAD_SCROLL_TIMEOUT = 500;

export const useScroll = () => {
  const [firstMessage, setFirstMessage] = useState<null | Message>(null);
  const { scrollAction, setScrollAction } = useCommunityInboxStore();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollHeightBeforePaginationRef = useRef<number>(0);
  const scrollTopBeforePaginationRef = useRef<number>(0);

  const preparePagination = useCallback(() => {
    const container = document.getElementById(MESSAGE_CONTAINER_ID);
    if (container) {
      scrollHeightBeforePaginationRef.current = container.scrollHeight;
      scrollTopBeforePaginationRef.current = container.scrollTop;
    }
  }, []);

  const handlePagination = useCallback(() => {
    const container = document.getElementById(MESSAGE_CONTAINER_ID);
    if (!container) return;

    const previousHeight = scrollHeightBeforePaginationRef.current;
    const previousTop = scrollTopBeforePaginationRef.current;

    if (previousHeight) {
      const heightDiff = container.scrollHeight - previousHeight;
      container.scrollTop = previousTop + heightDiff;
    } else if (firstMessage) {
      const elementId = firstMessage.id?.toString();
      const element = elementId ? document.getElementById(elementId) : null;
      element?.scrollIntoView();
    }
  }, [firstMessage]);

  const handleFirstLoad = useCallback(() => {
    const element = document.getElementById(MESSAGE_CONTAINER_ID);
    if (!element) return;

    timeoutRef.current = setTimeout(() => {
      element.scrollTop = element.scrollHeight;
    }, FIRST_LOAD_SCROLL_TIMEOUT);
  }, []);

  const handleNewMessage = useCallback(() => {
    const element = document.getElementById(MESSAGE_CONTAINER_ID);
    if (!element) return;

    element.scrollTop = element.scrollHeight;
  }, []);

  const adjustScroller = useCallback(() => {
    if (timeoutRef.current && scrollAction === ACTIONS.PAGINATION) {
      clearTimeout(timeoutRef.current);
    }

    switch (scrollAction) {
      case ACTIONS.PAGINATION:
        handlePagination();
        break;
      case ACTIONS.FIRST_LOAD:
        handleFirstLoad();
        break;
      case ACTIONS.NEW_MESSAGE:
        handleNewMessage();
        break;
    }
  }, [scrollAction, handlePagination, handleFirstLoad, handleNewMessage]);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  return {
    setFirstMessage,
    setScrollAction,
    preparePagination,
    adjustScroller,
  };
};
